import { useState, useRef, useEffect, useMemo } from 'react'
import { Search, Tag, FileText, Package } from 'lucide-react'
import { Input } from '@/shared/ui/input'
import { useTea, useTeaware, useExtendedTeaSearch } from '@/entities/product/api/use-products'
import { useDrinkHistory } from '@/features/drink-history/api/use-drink-history'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useDebounce, cn } from '@/shared/lib/utils'
import { OptimizedImage } from '@/shared/ui'
import type { CatalogSection } from '@/widgets/catalog-section-switcher'

export interface HomeSearchProps {
  /**
   * Секция каталога для поиска
   */
  section?: CatalogSection
  /**
   * Синхронизировать поиск с URL
   */
  syncWithUrl?: boolean
  /**
   * Кастомный placeholder
   */
  placeholder?: string
}

/**
 * Виджет: Универсальный поиск
 * 
 * Объединенный поиск по:
 * - Чаю/посуде из каталога
 * - Чаю из дневника
 * - Заметкам
 */
export function HomeSearch({ 
  section = 'tea',
  syncWithUrl = false,
  placeholder: customPlaceholder,
}: HomeSearchProps = {}) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  
  // Получаем текущий поиск из URL (если syncWithUrl)
  let currentSearch: string | undefined
  if (syncWithUrl) {
    try {
      const search = useSearch({ strict: false })
      currentSearch = (search as any)?.search
    } catch {
      if (typeof window !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search)
        currentSearch = searchParams.get('search') || undefined
      }
    }
  }
  
  const [query, setQuery] = useState(currentSearch || '')
  const [isFocused, setIsFocused] = useState(false)
  const debouncedQuery = useDebounce(query, 300)
  
  // Синхронизируем с URL
  useEffect(() => {
    if (syncWithUrl && currentSearch !== query && currentSearch !== undefined) {
      setQuery(currentSearch)
    }
  }, [currentSearch, syncWithUrl])
  
  // Обновляем URL при изменении поиска
  useEffect(() => {
    if (syncWithUrl) {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          search: debouncedQuery || undefined,
        }),
        replace: true,
      } as any)
    }
  }, [debouncedQuery, navigate, syncWithUrl])
  
  // Расширенный поиск для чая (по названию, стилю)
  const teaSearchQuery = useExtendedTeaSearch(
    section === 'tea' ? debouncedQuery : '',
    5
  )
  
  // Поиск посуды (простой)
  const teawareSearchQuery = useTeaware({
    search: section === 'teaware' ? debouncedQuery : '',
    limit: 5,
  })
  
  // Поиск в дневнике (только для чая)
  const { data: diaryResults } = useDrinkHistory({
    limit: 50, // Загружаем больше для локальной фильтрации
  })
  
  // Фильтруем записи дневника по поисковому запросу
  const filteredDiaryEntries = useMemo(() => {
    if (section !== 'tea' || !debouncedQuery.trim()) return []
    
    const query = debouncedQuery.toLowerCase()
    return (diaryResults?.data || []).filter(entry => {
      // Поиск по заметкам
      if (entry.notes?.toLowerCase().includes(query)) return true
      // Поиск по названию чая
      if (entry.teaName?.toLowerCase().includes(query)) return true
      // Поиск по категории чая
      if (entry.tea?.categoryName?.toLowerCase().includes(query)) return true
      return false
    }).slice(0, 5)
  }, [diaryResults?.data, debouncedQuery, section])
  
  // Показываем результаты если есть текст в поиске (даже во время debounce)
  const showResults = isFocused && query.length > 0
  
  // Идёт ли debounce (пользователь печатает)
  const isDebouncing = query !== debouncedQuery
  
  // Результаты для чая (структура: teaSearchQuery.data.data.byName)
  const byNameResults = teaSearchQuery?.data?.data?.byName || []
  const byStyleResults = teaSearchQuery?.data?.data?.byStyle || []
  const categoryMatches = teaSearchQuery?.data?.data?.categories || []
  
  // Результаты для посуды
  const teawareItems = teawareSearchQuery?.data?.data || []
  
  // Состояние загрузки: показываем скелетоны если идёт debounce ИЛИ загрузка данных
  const isSearchLoading = isDebouncing || (section === 'tea' 
    ? teaSearchQuery?.isLoading || teaSearchQuery?.isFetching
    : teawareSearchQuery?.isLoading || teawareSearchQuery?.isFetching)
  
  const hasResults = section === 'tea'
    ? byNameResults.length > 0 || byStyleResults.length > 0 || categoryMatches.length > 0 || filteredDiaryEntries.length > 0
    : teawareItems.length > 0
  
  // Placeholder по умолчанию в зависимости от секции
  const placeholder = customPlaceholder || (section === 'tea' 
    ? 'Найти чай, направление или заметку в дневнике'
    : 'Найти посуду')
  
  const handleClear = () => {
    setQuery('')
    inputRef.current?.focus()
    if (syncWithUrl) {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          search: undefined,
        }),
      } as any)
    }
  }
  
  const handleProductClick = (productId: string) => {
    setIsFocused(false)
    navigate({
      to: '/product/$productId',
      params: { productId },
    })
  }
  
  const handleDiaryEntryClick = () => {
    setIsFocused(false)
    navigate({ to: '/tea-diary' })
  }
  
  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Задержка для обработки клика по результатам
            setTimeout(() => setIsFocused(false), 200)
          }}
          placeholder={placeholder}
          leftIcon={<Search className={cn(
            "h-5 w-5 sm:h-6 sm:w-6 transition-all duration-200",
            "text-gray-400 dark:text-gray-500",
            isFocused && "text-primary dark:text-primary-light scale-[1.05]"
          )} />}
          clearable
          onClear={handleClear}
          className={cn(
            "h-[52px] sm:h-[56px] text-base rounded-[16px]",
            "bg-white/95 dark:bg-[#1A1C1E]",
            "border border-silk/30 dark:border-dark-border/30",
            "placeholder:text-gray-500 dark:placeholder:text-gray-400 placeholder:opacity-70",
            "shadow-sm dark:shadow-md",
            "transition-all duration-200",
            isFocused && [
              "shadow-lg dark:shadow-xl",
              "border-primary/30 dark:border-primary-dark/30"
            ]
          )}
        />
        
        {/* Результаты поиска */}
        {showResults && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-card rounded-xl shadow-xl dark:shadow-2xl border border-silk dark:border-dark-border max-h-[60vh] overflow-y-auto z-50">
            {isSearchLoading ? (
              <div className="px-4 py-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : hasResults ? (
              <div className="py-2">
                {section === 'tea' ? (
                  <>
                    {/* По названию */}
                    {byNameResults.length > 0 && (
                      <div>
                        <div className="px-4 py-2 flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            По названию
                          </span>
                        </div>
                        {byNameResults.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => handleProductClick(item.id)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated transition-colors touch-target flex items-center gap-3"
                          >
                            <OptimizedImage
                              src={item.images?.[0]?.url || '/placeholder-product.svg'}
                              alt={item.name}
                              preset="thumb"
                              wrapperClassName="w-10 h-10 rounded-lg flex-shrink-0 bg-gray-100 dark:bg-gray-800"
                              className="object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-ink dark:text-white truncate">{item.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {item.subcategoryName 
                                  ? `${item.categoryName} → ${item.subcategoryName}`
                                  : item.categoryName}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* По направлениям (категории) */}
                    {(categoryMatches.length > 0 || byStyleResults.length > 0) && (
                      <div>
                        <div className="px-4 py-2 flex items-center gap-2">
                          <Tag className="w-4 h-4 text-gray-400" />
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            По направлениям
                          </span>
                        </div>
                        {/* Найденные категории */}
                        {categoryMatches.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              setIsFocused(false)
                              navigate({
                                to: '/catalog/$section',
                                params: { section: 'tea' },
                                search: { category: cat.id },
                              })
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated transition-colors touch-target flex items-center gap-3"
                          >
                            <div className="w-10 h-10 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                              <Tag className="w-5 h-5 text-primary dark:text-primary-light" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-ink dark:text-white truncate">{cat.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {cat.type === 'category' ? 'Направление' : 'Подкатегория'} • {cat.teaCount} {cat.teaCount === 1 ? 'чай' : 'чаёв'}
                              </div>
                            </div>
                          </button>
                        ))}
                        {/* Чаи из найденных категорий */}
                        {byStyleResults.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => handleProductClick(item.id)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated transition-colors touch-target flex items-center gap-3"
                          >
                            <OptimizedImage
                              src={item.images?.[0]?.url || '/placeholder-product.svg'}
                              alt={item.name}
                              preset="thumb"
                              wrapperClassName="w-10 h-10 rounded-lg flex-shrink-0 bg-gray-100 dark:bg-gray-800"
                              className="object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-ink dark:text-white truncate">{item.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {item.subcategoryName 
                                  ? `${item.categoryName} → ${item.subcategoryName}`
                                  : item.categoryName}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* По заметкам */}
                    {filteredDiaryEntries.length > 0 && (
                      <div>
                        <div className="px-4 py-2 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            В заметках
                          </span>
                        </div>
                        {filteredDiaryEntries.map((entry) => (
                          <button
                            key={entry.id}
                            onClick={() => {
                              setIsFocused(false)
                              if (entry.teaId) {
                                handleProductClick(entry.teaId)
                              } else {
                                handleDiaryEntryClick()
                              }
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated transition-colors touch-target"
                          >
                            <div className="font-medium text-ink dark:text-white">{entry.teaName || 'Чай'}</div>
                            {entry.tea?.categoryName && (
                              <div className="text-xs text-primary dark:text-primary-light mt-0.5">
                                {entry.tea.categoryName}
                              </div>
                            )}
                            {entry.notes && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                                {entry.notes}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  /* Посуда - простой поиск */
                  teawareItems.length > 0 && (
                    <div>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                        Посуда из каталога
                      </div>
                      {teawareItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleProductClick(item.id)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated transition-colors touch-target flex items-center gap-3"
                        >
                          <OptimizedImage
                            src={item.images?.[0]?.url || '/placeholder-product.svg'}
                            alt={item.name}
                            preset="thumb"
                            wrapperClassName="w-10 h-10 rounded-lg flex-shrink-0 bg-gray-100 dark:bg-gray-800"
                            className="object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-ink dark:text-white truncate">{item.name}</div>
                            {item.description && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                Ничего не найдено
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

